import {LayoutContentPromptBox} from "./LayoutContentPromptManagement.styled";
import {FormPrompt} from "../FormPrompt";
import ChatInput from "../../../../../elements/input/ChatInput";
import {observer} from "mobx-react";
import { useStore } from "../../../../../hooks";
import {useCallback, useRef, useEffect} from "react";
import ChatWindowComponent from "../../../../../elements/ChatBox/ChatWindowComponent";
import { UCPAgentVersion } from "../../../../../utils/enums";

export const LayoutContentPromptManagement = observer(() => {
  const { authStore, assistanceUCPStore, workspaceStore } = useStore();
  const currentWorkspaceUuid = authStore.currentWorkspaceUuid;
  const currentWorkspace = workspaceStore?.currentWorkspace;
  const textAreaRef = useRef();
  const bottomRef = useRef();

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      if (assistanceUCPStore.agentResponseSubscription) {
        assistanceUCPStore.agentResponseSubscription.unsubscribe();
        assistanceUCPStore.agentResponseSubscription = null;
      }
      assistanceUCPStore.cleanupConversation();
    };
  }, [assistanceUCPStore]);

  // Initialize agent on mount
  useEffect(() => {
    const initializeAgent = async () => {
      if (currentWorkspaceUuid) {
        await assistanceUCPStore.fetchConciergeAgent(currentWorkspaceUuid);
      }
    };
    initializeAgent();
  }, [currentWorkspaceUuid, assistanceUCPStore]);

  const handleKeyPress = useCallback(
    (e, callback) => {
      if (e.key === "Enter" && !e.shiftKey && !assistanceUCPStore.isSendMessageClicked) {
        e.preventDefault();
        const messageContent = assistanceUCPStore.newMessage.trim();
        if (messageContent) {
          handleSendMessage(messageContent);
        }
        callback("");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assistanceUCPStore],
  );

  const handleSendMessage = async (messageContent) => {
    if (!messageContent?.trim()) return;
    
    // Set agent version to BETA to enable LangChain
    assistanceUCPStore.setAgentVersion(UCPAgentVersion.BETA);
    
    // Use the store's handleSendMessage method
    await assistanceUCPStore.handleSendMessage(
      messageContent,
      {
        contact_full_name: currentWorkspace?.tenant_name || currentWorkspace?.name || '',
        contact_id: currentWorkspaceUuid
      },
      currentWorkspace || (currentWorkspaceUuid ? { id: currentWorkspaceUuid } : null),
      true, // isAnnUCP
      {
        ucp: {
          name: currentWorkspace?.tenant_name || currentWorkspace?.name || '',
          segment_id: currentWorkspaceUuid
        }
      }
    );
  };

  const handleCleanThread = () => {
    assistanceUCPStore.cleanupConversation();
  };

  const handleFileUpload = (attachment) => {
    if (attachment) {
      assistanceUCPStore.setAttachment(attachment);
    }
  };

  const handleStopClick = () => {
    assistanceUCPStore.setShowStopButton(false);
    if (assistanceUCPStore.agentResponseSubscription) {
      assistanceUCPStore.agentResponseSubscription.unsubscribe();
      assistanceUCPStore.agentResponseSubscription = null;
    }
    assistanceUCPStore.setAgentIsTyping(false);
    assistanceUCPStore.setIsSendMessageClicked(false);
  };
  
  return (
    <>
      <LayoutContentPromptBox>
        <FormPrompt />
      </LayoutContentPromptBox>
      <LayoutContentPromptBox style={{ display: 'flex', flexDirection: 'column', height: 'calc(106vh - 200px)' }}>
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '10px', minHeight: 0 }}>
          <ChatWindowComponent
            messages={assistanceUCPStore.conversationHistory.map(msg => ({
              ...msg,
              content: msg.message,
              timestamp: msg.time
            }))}
            bottomRef={bottomRef}
            width={"inherit"}
            height={"inherit"}
            agentIsTyping={assistanceUCPStore.agentIsTyping}
            backgroundColor={"#ffffff"}
            selectedAgent={assistanceUCPStore.conciergeAgent ? {
              name: assistanceUCPStore.conciergeAgent.name || "AI Assistant"
            } : {name: "AI Assistant"}}
          />
        </div>
        <div style={{ position: 'sticky', bottom: 0, backgroundColor: '#FAFAFA' }}>
          <ChatInput
            width={"100%"}
            customerId={currentWorkspaceUuid}
            textAreaRef={textAreaRef}
            onChange={(e) => assistanceUCPStore.setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            showSpinner={assistanceUCPStore.showSpinner}
            newMessage={assistanceUCPStore.newMessage}
            handleSendMessage={handleSendMessage}
            onFileUploaded={handleFileUpload}
            disableSend={assistanceUCPStore.isSendMessageClicked || !assistanceUCPStore.conciergeAgent}
            onCleanThread={handleCleanThread}
            stop={assistanceUCPStore.showStopButton}
            onStopClick={handleStopClick}
          />
        </div>
      </LayoutContentPromptBox>
    </>
  );  
});
